import { optOutOfAutofill } from '../util/autofill-opt-out';

declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

/**
 * What the field says when a query matches nothing.
 *
 * Mapbox's own "No results found" is a dead end: the most common reason a
 * search here finds nothing is that the visitor typed a place outside the
 * United States, which is the whole of our coverage and nowhere stated in the
 * UI. So the message names the limit and repeats what the field does accept.
 *
 * Carries the plugin's own class names so its stylesheet - and ours in
 * `app.component.scss` - still reach it.
 */
const NO_RESULTS_HTML = `<div class="mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results">
  Nothing found. Search covers the United States only - try a US address, ZIP code, city, or state.
</div>`;

/**
 * Replaces the plugin's no-results message on one geocoder instance.
 *
 * `_renderNoResults` is private API, which is the price of the plugin
 * exposing no option for this text: `render` only formats suggestions, and
 * the string is otherwise hardcoded (see mapbox-gl-geocoder v5). Overriding
 * the instance rather than the prototype keeps it to the geocoders we build.
 */
export function useUsOnlyNoResultsMessage(geocoder: MapboxGeocoder): void {
  const plugin = geocoder as any;
  plugin._renderNoResults = () => plugin._renderMessage(NO_RESULTS_HTML);
}

/**
 * Keeps autofill menus off the plugin's field (see `autofill-opt-out.ts`).
 * The plugin sets no `autocomplete` and builds a new input on every `onAdd`,
 * which `addTo` and `map.addControl` both go through, so the hook sits there.
 */
export function keepAutofillOff(geocoder: MapboxGeocoder): void {
  const plugin = geocoder as any;
  const onAdd = plugin.onAdd;
  plugin.onAdd = function (this: any, ...args: unknown[]) {
    const container = onAdd.apply(this, args);
    if (plugin._inputEl) optOutOfAutofill(plugin._inputEl);
    return container;
  };
}
